import React from 'react';
import PropTypes from 'prop-types';

import { useQuery } from '@apollo/client';

import { generateSingleItemQuery, getCount } from '../../application/query';
import {
  buildBannerSizes,
  buildContextualTargeting,
  buildParsedAdunit,
  buildSizeMapping,
  setCustomTargetings
} from './helpers';
import {
  BANNER_QUERY_PROPS,
  COUNT_CONTENT_HIERARCHIES,
  GET_BANNER,
  PUBLISHED
} from '../../constants';
import { withTitle } from '../../HOC';
import AdSlotRender from './AdSlotRender';

const BannerRender = ({
  baseAdunit,
  adunit,
  sizeId,
  sizes,
  propsToDisplay,
  entity,
  targetings,
  parent,
  asPath,
  action,
  actionKey,
  renderCounter,
  userPropsData,
  modifiers,
  ...otherProps
}) => {
  const { id } = otherProps;
  const { itemId } = parent;

  const { data: entityRecordData = {} } = useQuery(action, {
    variables: { where: { id: id || itemId } },
    skip: id && id !== itemId
  });

  const { data: bannerData = {} } = useQuery(
    generateSingleItemQuery(GET_BANNER, BANNER_QUERY_PROPS),
    {
      variables: { id: sizeId },
      skip: !sizeId
    }
  );

  const { data = {} } = useQuery(getCount(COUNT_CONTENT_HIERARCHIES), {
    variables: {
      where: {
        parentId: id || itemId,
        childEntity: {
          _ilike: `${PUBLISHED}_%`
        }
      }
    }
  });

  const parsedTargetings = buildContextualTargeting(actionKey, entityRecordData, propsToDisplay);
  const { entityData } = bannerData;
  const { sizes: bannerSizes } = entityData || {};

  const sizeMapping = buildSizeMapping(sizeId, bannerSizes);
  const parsedSizes = buildBannerSizes(sizeId, bannerSizes, sizes);

  const { countContentHierarchies } = data;

  const parsedAdunit = buildParsedAdunit(asPath, countContentHierarchies, baseAdunit, adunit);
  const isContextualTargetingSet =
    propsToDisplay.length && parsedTargetings && Object.keys(parsedTargetings).length;
  const isContextualTargetingReady = isContextualTargetingSet || !propsToDisplay.length;
  const shouldShowBanner =
    parsedAdunit && parsedSizes && !!parsedSizes.length && isContextualTargetingReady;

  const customTargetings = setCustomTargetings(targetings, renderCounter);

  const targetingArguments = { ...parsedTargetings, ...customTargetings, ...userPropsData };

  return (
    <AdSlotRender
      shouldShowBanner={!!shouldShowBanner}
      sizeMapping={sizeMapping}
      sizeId={sizeId}
      parsedAdunit={parsedAdunit}
      parsedSizes={parsedSizes}
      modifiers={modifiers}
      targetingArguments={targetingArguments}
    />
  );
};

BannerRender.propTypes = {
  parent: PropTypes.object.isRequired,
  asPath: PropTypes.string.isRequired,
  action: PropTypes.object.isRequired,
  actionKey: PropTypes.string.isRequired,
  adunit: PropTypes.string,
  baseAdunit: PropTypes.string,
  sizeId: PropTypes.string,
  propsToDisplay: PropTypes.array,
  entity: PropTypes.string,
  targetings: PropTypes.string,
  sizes: PropTypes.string,
  renderCounter: PropTypes.number,
  modifiers: PropTypes.string,
  userPropsData: PropTypes.object
};

BannerRender.defaultProps = {
  adunit: '',
  baseAdunit: '',
  sizeId: '',
  propsToDisplay: [],
  entity: '',
  targetings: '',
  sizes: '',
  renderCounter: null,
  modifiers: '',
  userPropsData: {}
};

export default withTitle(BannerRender);
